<template>
  <div class="container-fluid">
    <!-- Header con titolo e azioni -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h2>Gestione Organizzazioni</h2>
        <p class="text-muted">Amministrazione organizzazioni private e pubbliche</p>
      </div>
      <div class="col-md-6 text-end">
        <button 
          class="btn btn-primary" 
          @click="openCreateDialog"
        >
          <i class="fa fa-plus-circle"></i> Nuova Organizzazione
        </button>        
      </div>
    </div>

    <!-- Barra di ricerca -->
    <div class="row mb-3">
      <div class="col-md-6">
        <div class="input-group">
          <input 
            type="text" 
            class="form-control" 
            placeholder="Cerca per nome o sigla..."
            v-model="searchTerm"
            @input="performSearch"
          >
          <button class="btn btn-outline-secondary" @click="clearSearch">
            <i class="fa fa-times-circle"></i>
          </button>
        </div>
      </div>
      <div class="col-md-6">
        <select class="form-select" v-model="filterType" @change="applyFilters">
          <option value="">Tutti i tipi</option>
          <option value="0">Solo Privata</option>
          <option value="1">Solo Pubblica</option>
        </select>
      </div>
    </div>

    <!-- Alert per messaggi -->
    <div v-if="alertMessage" :class="`alert alert-${alertType} alert-dismissible fade show`" role="alert">
      {{ alertMessage }}
      <button type="button" class="btn-close" @click="hideAlert"></button>
    </div>

    <!-- Statistiche Panel (se richiesto) -->
    <div v-if="showStats && stats" class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5><i class="fa fa-bar-chart"></i> Statistiche Organizzazioni</h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <div class="text-center">
                  <h3 class="text-primary">{{ stats.total_organisations }}</h3>
                  <p class="text-muted">Totale</p>
                </div>
              </div>
              <div class="col-md-9">
                <div class="row">
                  <div v-for="stat in stats.stats_by_type" :key="stat.tipo" class="col-md-6">
                    <div class="text-center">
                      <h4>{{ stat.total }}</h4>
                      <p class="text-muted">{{ stat.tipo_label }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabella organizzazioni -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5>
              <i class="fa fa-building"></i> 
              Elenco Organizzazioni 
              <span class="badge bg-secondary ms-2">{{ filteredOrganisations.length }}</span>
            </h5>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Caricamento...</span>
              </div>
            </div>

            <div v-else-if="filteredOrganisations.length === 0" class="text-center py-4">
              <i class="fa fa-inbox fa-5x text-muted"></i>
              <p class="text-muted mt-3">Nessuna organizzazione trovata</p>
              <button class="btn btn-primary" @click="openCreateDialog">
                Crea la prima organizzazione
              </button>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead class="table-light">
                  <tr>
                    <th>Nome</th>
                    <th>Sigla</th>
                    <th>Tipo</th>
                    <th>Ultima Modifica</th>
                    <th class="text-center">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="organisation in filteredOrganisations" :key="organisation.id_organisation">
                    <td>
                      <strong>{{ organisation.nome }}</strong>
                    </td>
                    <td>
                      <span class="badge bg-secondary">{{ organisation.sigla }}</span>
                    </td>
                    <td>
                      <span :class="`badge ${organisation.tipo === 0 ? 'bg-primary' : 'bg-success'}`">
                        {{ organisation.tipo_label }}
                      </span>
                    </td>
                    <td>
                      <small class="text-muted">
                        {{ formatDate(organisation.updated_timestamp) }}
                      </small>
                    </td>
                    <td class="text-center">
                      <div class="btn-group" role="group">
                        <button 
                          class="btn btn-sm btn-outline-primary" 
                          @click="openEditDialog(organisation)"
                          title="Modifica"
                        >
                          <i class="fa fa-pencil"></i>
                        </button>
                        <button 
                          class="btn btn-sm btn-outline-danger" 
                          @click="confirmDelete(organisation)"
                          title="Elimina"
                        >
                          <i class="fa fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog Modal per Create/Edit -->
    <div 
      v-if="showDialog" 
      class="modal fade show" 
      tabindex="-1"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      @click="handleModalClick"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              {{ isEditMode ? 'Modifica Organizzazione' : 'Nuova Organizzazione' }}
            </h5>
            <button type="button" class="btn-close" @click="closeDialog"></button>
          </div>
          <form @submit.prevent="saveOrganisation">
            <div class="modal-body">
              <!-- Nome -->
              <div class="mb-3">
                <label for="nome" class="form-label">Nome Organizzazione *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="nome"
                  v-model="formData.nome"
                  :class="{'is-invalid': validationErrors.nome}"
                  placeholder="Inserisci il nome completo dell'organizzazione"
                  maxlength="255"
                  required
                >
                <div v-if="validationErrors.nome" class="invalid-feedback">
                  {{ validationErrors.nome }}
                </div>
              </div>

              <!-- Sigla -->
              <div class="mb-3">
                <label for="sigla" class="form-label">Sigla *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="sigla"
                  v-model="formData.sigla"
                  :class="{'is-invalid': validationErrors.sigla}"
                  placeholder="Sigla (max 10 caratteri)"
                  maxlength="10"
                  required
                >
                <div v-if="validationErrors.sigla" class="invalid-feedback">
                  {{ validationErrors.sigla }}
                </div>
              </div>

              <!-- Tipo -->
              <div class="mb-3">
                <label for="tipo" class="form-label">Tipo Organizzazione *</label>
                <select 
                  class="form-select" 
                  id="tipo"
                  v-model="formData.tipo"
                  :class="{'is-invalid': validationErrors.tipo}"
                  required
                >
                  <option value="">Seleziona tipo...</option>
                  <option value="0">Privata</option>
                  <option value="1">Pubblica</option>
                </select>
                <div v-if="validationErrors.tipo" class="invalid-feedback">
                  {{ validationErrors.tipo }}
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeDialog">
                Annulla
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                {{ isEditMode ? 'Aggiorna' : 'Crea' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Dialog Conferma Eliminazione -->
    <div 
      v-if="showDeleteDialog" 
      class="modal fade show" 
      tabindex="-1"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      @click="handleDeleteModalClick"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title text-danger">
              <i class="fa fa-exclamation-triangle"></i> Conferma Eliminazione
            </h5>
            <button type="button" class="btn-close" @click="closeDeleteDialog"></button>
          </div>
          <div class="modal-body">
            <p>Sei sicuro di voler eliminare l'organizzazione:</p>
            <div class="alert alert-warning">
              <strong>{{ organisationToDelete?.nome }}</strong> ({{ organisationToDelete?.sigla }})
            </div>
            <p class="text-muted">Questa azione non può essere annullata.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDeleteDialog">
              Annulla
            </button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="executeDelete"
              :disabled="deleting"
            >
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
              Elimina
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrganisationAPI from '../services/organisationAPI.js';

export default {
  name: 'OrganisationManagement',
  data() {
    return {
      // API client
      organisationAPI: new OrganisationAPI(),
      
      // Dati organizzazioni
      organisations: [],
      filteredOrganisations: [],
      
      // Stato UI
      loading: false,
      saving: false,
      deleting: false,
      
      // Dialog modals
      showDialog: false,
      showDeleteDialog: false,
      isEditMode: false,
      organisationToDelete: null,
      
      // Form data
      formData: {
        nome: '',
        sigla: '',
        tipo: ''
      },
      
      // Validazione
      validationErrors: {},
      
      // Alert system
      alertMessage: '',
      alertType: 'info',
      
      // Filtri e ricerca
      searchTerm: '',
      filterType: '',
      
      // Statistiche
      showStats: false,
      stats: null
    };
  },
  
  async mounted() {
    await this.loadOrganisations();
  },
  
  methods: {
    // ============================================================================
    // CARICAMENTO DATI
    // ============================================================================
    
    async loadOrganisations() {
      this.loading = true;
      
      try {
        const result = await this.organisationAPI.getOrganisations();
        
        if (result.ok) {
          this.organisations = result.data || [];
          this.applyFilters();
          console.log('Organizzazioni caricate:', this.organisations.length);
        } else {
          this.showAlert('danger', result.message || 'Errore nel caricamento organizzazioni');
        }
      } catch (error) {
        this.showAlert('danger', `Errore: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },
    
    async loadStats() {
      try {
        const result = await this.organisationAPI.getOrganisationStats();
        
        if (result.ok) {
          this.stats = result.data;
          this.showStats = true;
          this.showAlert('info', 'Statistiche aggiornate');
        } else {
          this.showAlert('danger', result.message || 'Errore nel caricamento statistiche');
        }
      } catch (error) {
        this.showAlert('danger', `Errore: ${error.message}`);
      }
    },
    
    // ============================================================================
    // GESTIONE MODAL CREATE/EDIT
    // ============================================================================
    
    openCreateDialog() {
      this.isEditMode = false;
      this.resetForm();
      this.showDialog = true;
    },
    
    openEditDialog(organisation) {
      this.isEditMode = true;
      this.formData = {
        id_organisation: organisation.id_organisation,
        nome: organisation.nome,
        sigla: organisation.sigla,
        tipo: organisation.tipo
      };
      this.validationErrors = {};
      this.showDialog = true;
    },
    
    async saveOrganisation() {
      // Reset validazione
      this.validationErrors = {};
      
      // Validazione lato client
      const validation = this.organisationAPI.validateOrganisationData(this.formData);
      if (!validation.isValid) {
        validation.errors.forEach(error => {
          if (error.includes('Nome')) this.validationErrors.nome = error;
          if (error.includes('Sigla')) this.validationErrors.sigla = error;
          if (error.includes('Tipo')) this.validationErrors.tipo = error;
        });
        return;
      }
      
      this.saving = true;
      
      try {
        let result;
        
        if (this.isEditMode) {
          result = await this.organisationAPI.updateOrganisation(
            this.formData.id_organisation, 
            this.formData
          );
        } else {
          result = await this.organisationAPI.createOrganisation(this.formData);
        }
        
        if (result.ok) {
          const action = this.isEditMode ? 'aggiornata' : 'creata';
          this.showAlert('success', `Organizzazione ${action} con successo`);
          
          this.closeDialog();
          await this.loadOrganisations();
        } else {
          this.showAlert('danger', result.message || 'Errore nel salvataggio');
        }
      } catch (error) {
        this.showAlert('danger', `Errore: ${error.message}`);
      } finally {
        this.saving = false;
      }
    },
    
    // ============================================================================
    // GESTIONE ELIMINAZIONE
    // ============================================================================
    
    confirmDelete(organisation) {
      this.organisationToDelete = organisation;
      this.showDeleteDialog = true;
    },
    
    async executeDelete() {
      if (!this.organisationToDelete) return;
      
      this.deleting = true;
      
      try {
        const result = await this.organisationAPI.deleteOrganisation(
          this.organisationToDelete.id_organisation
        );
        
        if (result.ok) {
          this.showAlert('success', 'Organizzazione eliminata con successo');
          this.closeDeleteDialog();
          await this.loadOrganisations();
        } else {
          this.showAlert('danger', result.message || 'Errore nella eliminazione');
        }
      } catch (error) {
        this.showAlert('danger', `Errore: ${error.message}`);
      } finally {
        this.deleting = false;
        this.organisationToDelete = null;
      }
    },
    
    // ============================================================================
    // RICERCA E FILTRI
    // ============================================================================
    
    async performSearch() {
      if (this.searchTerm.trim().length >= 2) {
        try {
          const result = await this.organisationAPI.searchOrganisations(this.searchTerm);
          if (result.ok) {
            this.filteredOrganisations = result.data || [];
          }
        } catch (error) {
          console.error('Errore ricerca:', error);
        }
      } else {
        this.applyFilters();
      }
    },
    
    clearSearch() {
      this.searchTerm = '';
      this.applyFilters();
    },
    
    applyFilters() {
      let filtered = [...this.organisations];
      
      // Filtro per tipo
      if (this.filterType !== '') {
        filtered = filtered.filter(org => org.tipo == this.filterType);
      }
      
      this.filteredOrganisations = filtered;
    },
    
    // ============================================================================
    // UTILITY METHODS
    // ============================================================================
    
    resetForm() {
      this.formData = {
        nome: '',
        sigla: '',
        tipo: ''
      };
      this.validationErrors = {};
    },
    
    closeDialog() {
      this.showDialog = false;
      this.resetForm();
    },
    
    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.organisationToDelete = null;
    },
    
    handleModalClick(event) {
      // Modale non si chiude quando si clicca fuori
      // if (event.target === event.currentTarget) {
      //   this.closeDialog();
      // }
    },
    
    handleDeleteModalClick(event) {
      // Modale non si chiude quando si clicca fuori
      // if (event.target === event.currentTarget) {
      //   this.closeDeleteDialog();
      // }
    },
    
    showAlert(type, message) {
      this.alertType = type;
      this.alertMessage = message;
      
      // Auto-hide dopo 5 secondi per messaggi di successo
      if (type === 'success') {
        setTimeout(() => {
          this.hideAlert();
        }, 5000);
      }
    },
    
    hideAlert() {
      this.alertMessage = '';
    },
    
    formatDate(timestamp) {
      if (!timestamp) return '';
      return new Date(timestamp).toLocaleDateString('it-IT');
    }
  }
};
</script>

<style scoped>
/* Stili specifici per il componente */
.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.table th {
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
}

.btn-group .btn {
  margin-right: 0;
}

.badge {
  font-size: 0.75em;
}

.modal-header {
  background-color: #f8f9fa;
}

.alert {
  margin-bottom: 1rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .btn-group .btn {
    padding: 0.25rem 0.5rem;
  }
}

/* Dialog Modal Styles */
.modal.show {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-dialog {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from { 
    opacity: 0;
    transform: translateY(-50px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-content {
  border: none;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.modal-title {
  font-weight: 600;
}
</style>